import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SortingRule } from "react-table";
import { Col, Row } from "reactstrap";
import ButtonComponent from "../../components/ButtonComponent";
import InputComponent from "../../components/InputComponent";
import Layout from "../../components/Layout/Layout";
import TableComponent, { OperationsCell } from "../../components/TableComponent";
import Routes from "../../helpers/Routes";
import Toast from "../../helpers/Toast";
import { Utilities } from "../../helpers/Utilities";
import { MaintenancePlanSearchResultDTO } from "../../models/MaintenancePlan/MaintenancePlanSearchResultDTO";
import { Parameter } from "../../models/Parameter";
import { MaintenancePlanService } from "../../services/MaintenancePlanService";
import { SelectItemsProps } from "../../components/SearchSelectComponent";
import SelectComponent from "../../components/SelectComponent";
import SearchOrTypeSelectComponent from "../../components/SearchOrTypeSelectComponent";
import { EquipmentUtilities } from "../../EquipmentUtilities";

export default function MaintenancePlanIndex() {
    const intl = useIntl();

    const navigate = useNavigate();
    const maintenancePlanService = new MaintenancePlanService();
    const pageSizes: number[] = [10, 15, 30, 50];
    const [data, setData] = useState<MaintenancePlanSearchResultDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [pageCount, setPageCount] = useState(1);
    const [searchParams] = useSearchParams();
    const [page] = useState<number>(Utilities.LoadPageFromURLQuery("page"));
    const [pageSize] = useState<number>(Utilities.LoadPageSizeFromURLQuery(pageSizes, 10));

    const [maintenancePlanNumerator, setMaintenancePlanNumerator] = useState<string>(Utilities.LoadParameterFromURLQuery("maintenancePlanNumerator", "string", ""));
    const [maintenancePlanName, setMaintenancePlanName] = useState<string>(Utilities.LoadParameterFromURLQuery("maintenancePlanName", "string", ""));
    const [clientName, setClientName] = useState<string>(Utilities.LoadParameterFromURLQuery("clientName", "string", ""));
    const [equipmentSerialNumber, setEquipmentSerialNumber] = useState<string>(Utilities.LoadParameterFromURLQuery("equipmentSerialNumber", "string", ""));
    const [equipmentBrand, setEquipmentBrand] = useState<string>(Utilities.LoadParameterFromURLQuery("equipmentBrand", "string", ""));
    const [equipmentModel, setEquipmentModel] = useState<string>(Utilities.LoadParameterFromURLQuery("equipmentModel", "string", ""));
    const [status, setStatus] = useState<string>(Utilities.LoadParameterFromURLQuery("status", "string", String(true)));
    const [selectedEquipmentBrand, setSelectedEquipmentBrand] = useState<SelectItemsProps | null>(equipmentBrand ? { label: equipmentBrand, value: equipmentBrand } : null);
    const [selectedEquipmentModel, setSelectedEquipmentModel] = useState<SelectItemsProps | null>(equipmentModel ? { label: equipmentModel, value: equipmentModel } : null);

    const [currentPage, setCurrentPage] = useState<number>();
    const [currentPageSize, setCurrentPageSize] = useState<number>(10);
    const [currentSorting, setCurrentSorting] = useState<SortingRule<object>[] | null>(null);
    const [goToPage, setGoToPage] = useState<number>();
    const [initialSortBy] = useState<SortingRule<object>[]>(Utilities.LoadSortParametersFromURLQuery("sortField", "sortOrder"));
    const [reloadSearch, setReloadSearch] = useState<boolean>(false);

    // poderá ser substituido por intl no futuro
    const statusOptions: SelectItemsProps[] = [
        { value: String(true), label: "Ativo" },
        { value: String(false), label: "Inativo" }
    ];

    const columns = React.useMemo(
        () => [
            {
                Header: "",
                accessor: " ",
                Cell: (props: any) =>
                    OperationsCell(props.cell.row.original.id, "maintenancePlan"),
                width: 5,
            },
            {
                Header: <FormattedMessage id="Screens.MaintenancePlans.Table.MaintenancePlanNumerator" />,
                accessor: "numerator",
                width: 5,
            },
            {
                Header: <FormattedMessage id="Screens.MaintenancePlans.Table.ClientName" />,
                accessor: "clientName",
                width: 25,
            },
            {
                Header: <FormattedMessage id="Screens.MaintenancePlans.Table.EquipmentSerialNumber" />,
                accessor: "serialNumber",
                width: 15,
            },
            {
                Header: <FormattedMessage id="Screens.MaintenancePlans.Table.EquipmentBrand" />,
                accessor: "brand",
                width: 10,
            },
            {
                Header: <FormattedMessage id="Screens.MaintenancePlans.Table.EquipmentModel" />,
                accessor: "model",
                width: 10,
            },
            {
                Header: <FormattedMessage id="Screens.MaintenancePlans.Table.MaintenancePlanName" />,
                accessor: "name",
                width: 20,
            },
            {
                Header: <FormattedMessage id="Screens.MaintenancePlans.Table.Status" />,
                accessor: "status",
                width: 10,
            }
        ],
        []
    );

    const fetchData = async (
        page: number,
        pageSize: number,
        sortBy: SortingRule<object>[] | null
    ) => {
        //Atualizamos na history do browser a current page e pageSize

        var searchUrl: string = `?page=${page}&pageSize=${pageSize}` +
            `${Utilities.AddParamToQueryIfValue("maintenancePlanNumerator", "string", maintenancePlanNumerator)}` +
            `${Utilities.AddParamToQueryIfValue("maintenancePlanName", "string", maintenancePlanName)}` +
            `${Utilities.AddParamToQueryIfValue("clientName", "string", clientName)}` +
            `${Utilities.AddParamToQueryIfValue("equipmentSerialNumber", "string", equipmentSerialNumber)}` +
            `${Utilities.AddParamToQueryIfValue("equipmentBrand", "string", equipmentBrand)}` +
            `${Utilities.AddParamToQueryIfValue("equipmentModel", "string", equipmentModel)}` +
            `${Utilities.AddParamToQueryIfValue("status", "string", status)}`;

        if (sortBy != null && sortBy.length > 0) {
            searchUrl += `${Utilities.AddParamToQueryIfValue(
                "sortField",
                "string",
                sortBy[0].id
            )}${Utilities.AddParamToQueryIfValue(
                "sortOrder",
                "string",
                sortBy[0].desc === true ? "DESC" : "ASC"
            )}`;
        }

        //Se antes não existirem parâmetros de pesquisa, significa que o user veio para aqui de uma rota, então para não ficar com duas rotas duplicadas, fazemos replace à rota

        if (searchParams.toString() === "") {
            navigate(`/maintenancePlans${searchUrl}`, { replace: true });
        } else {
            //Se já existirem parâmetros de pesquisa, não fazemos replace mas push
            navigate(`/maintenancePlans${searchUrl}`);
        }

        setLoading(true);

        var response = await maintenancePlanService.GetAll(
            mountSearchParameters(),
            Utilities.MountSortParameters(sortBy),
            page,
            pageSize
        );

        if (response.success !== true) {
            setData([]);
            setPageCount(0);
            setLoading(false);
            setCurrentPageSize(10);
            setCurrentPage(1);
            setCurrentSorting(null);
            Toast.Show("error", response.message);
            return;
        }

        setData(response.items);
        setPageCount(response.totalPages);
        setLoading(false);
        setCurrentPageSize(pageSize);
        setCurrentPage(page);
        setCurrentSorting(sortBy);
        setGoToPage(0);

        if (response.totalPages > 0 && page > response.totalPages) {
            setGoToPage(1);
        }
    };

    const mountSearchParameters = (): Parameter[] => {
        var parameters: Parameter[] = [];
        parameters.push(new Parameter("maintenancePlanNumerator", maintenancePlanNumerator));
        parameters.push(new Parameter("maintenancePlanName", maintenancePlanName));
        parameters.push(new Parameter("clientName", clientName));
        parameters.push(new Parameter("equipmentSerialNumber", equipmentSerialNumber));
        parameters.push(new Parameter("equipmentBrand", equipmentBrand));
        parameters.push(new Parameter("equipmentModel", equipmentModel));
        parameters.push(new Parameter("status", status));

        return parameters;
    };

    const onEnter = () => {
        fetchData(currentPage ?? 1, currentPageSize ?? 10, currentSorting);
    };
    const onButtonClick = async () => {
        setCurrentPage(1);
        setGoToPage(1);
        fetchData(currentPage ?? 1, currentPageSize ?? 10, currentSorting);
    };
    const onRemoveFiltersClick = async () => {
        setCurrentPage(1);
        setGoToPage(1);
        setCurrentPageSize(10);

        setMaintenancePlanNumerator("");
        setMaintenancePlanName("");
        setClientName("");
        setEquipmentSerialNumber("");
        setEquipmentBrand("");
        setEquipmentModel("");
        setStatus("");
        setSelectedEquipmentBrand(null);
        setSelectedEquipmentModel(null);
        setReloadSearch(true);
    };

    useEffect(() => {
        if (reloadSearch === true) {
            fetchData(currentPage ?? 1, currentPageSize ?? 10, currentSorting);
            setReloadSearch(false);
        }
        return () => { };
    }, [reloadSearch]);



    const onNewTaskClick = () => {
        navigate(Routes.maintenancePlans.create);
    };



    return (
        <>
            <Layout>
                <div className="pageContainer">
                    <div className="pageHeader">
                        <span className="pageTitle">
                            <FormattedMessage id="Screens.MaintenancePlans.Title" />
                        </span>
                        <Row>
                            <Col
                                xl={2}
                                lg={3}
                                md={3}
                                sm={12}
                                xs={12}
                                style={{ marginTop: "1em", marginBottom: "1em" }}
                            >
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "Screens.MaintenancePlans.Button.CreateMaintenancePlan" })}
                                    onClick={onNewTaskClick}
                                    disabled={false} />
                            </Col>
                        </Row>
                    </div>

                    <div className="pageBody">
                        <Row>
                            <InputComponent
                                xl={1}
                                lg={1}
                                md={1}
                                sm={12}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                onEnter={onEnter}
                                value={maintenancePlanNumerator}
                                label={intl.formatMessage({ id: "Screens.MaintenancePlans.SearchField.MaintenancePlanNumerator" })}
                                readOnly={false}
                                onChange={(t: string) => setMaintenancePlanNumerator(t)}
                            />
                            <InputComponent
                                xl={2}
                                lg={2}
                                md={2}
                                sm={12}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                onEnter={onEnter}
                                value={clientName}
                                label={intl.formatMessage({ id: "Screens.MaintenancePlans.SearchField.ClientName" })}
                                readOnly={false}
                                onChange={(t: string) => setClientName(t)}
                            />
                            <InputComponent
                                xl={1}
                                lg={1}
                                md={1}
                                sm={12}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                onEnter={onEnter}
                                value={equipmentSerialNumber}
                                label={intl.formatMessage({ id: "Screens.MaintenancePlans.SearchField.EquipmentSerialNumber" })}
                                readOnly={false}
                                onChange={(t: string) => setEquipmentSerialNumber(t)}
                            />
                            <SearchOrTypeSelectComponent
                                xl={2}
                                lg={2}
                                md={2}
                                sm={12}
                                xs={12}
                                label={intl.formatMessage({ id: "Screens.MaintenancePlans.SearchField.EquipmentBrand" })}
                                value={selectedEquipmentBrand}
                                loadOptions={EquipmentUtilities.SearchBrand}
                                onChange={(selected) => {
                                    setSelectedEquipmentBrand(selected);
                                    setEquipmentBrand(selected?.value || "");
                                }}
                                isDisabled={false}
                            />

                            <SearchOrTypeSelectComponent
                                xl={2}
                                lg={2}
                                md={2}
                                sm={12}
                                xs={12}
                                label={intl.formatMessage({ id: "Screens.MaintenancePlans.SearchField.EquipmentModel" })}
                                value={selectedEquipmentModel}
                                loadOptions={EquipmentUtilities.SearchModel}
                                onChange={(selected) => {
                                    setSelectedEquipmentModel(selected);
                                    setEquipmentModel(selected?.value || "");
                                }}
                                isDisabled={false}
                            />
                            <InputComponent
                                xl={2}
                                lg={2}
                                md={2}
                                sm={12}
                                xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                onEnter={onEnter}
                                value={maintenancePlanName}
                                label={intl.formatMessage({ id: "Screens.MaintenancePlans.SearchField.MaintenancePlanName" })}
                                readOnly={false}
                                onChange={(t: string) => setMaintenancePlanName(t)}
                            />
                            <SelectComponent
                                xl={1}
                                lg={1}
                                md={1}
                                sm={12}
                                xs={12}
                                style={{ width: "100%" }}
                                label={intl.formatMessage({ id: "Screens.MaintenancePlans.SearchField.Status" })}
                                selectedItemValue={status}
                                items={statusOptions}
                                onChange={(t: string) => {
                                    setStatus(t);
                                }}
                            />
                        </Row>

                        <Row>
                            <Col
                                xl={6}
                                lg={6}
                                md={6}
                                sm={12}
                                xs={12}
                                style={{ marginTop: "1em", display: "inline-flex", gap: "1rem" }}
                            >
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "Common.Buttons.Search" })}
                                    onClick={onButtonClick}
                                    style={{ minWidth: "100px" }}
                                />
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "Common.Buttons.RemoveFilters" })}
                                    onClick={onRemoveFiltersClick}
                                    style={{ minWidth: "113px" }}
                                />
                            </Col>
                        </Row>

                        <div className="pageComponent" style={{ width: "100%" }}>
                            <TableComponent
                                goToPage={goToPage}
                                loading={loading}
                                pageCount={pageCount}
                                columns={columns}
                                manualPagination={true}
                                fetchData={fetchData}
                                pageSizes={pageSizes}
                                initialPage={page}
                                initialPageSize={pageSize}
                                initialSortBy={initialSortBy}
                                data={data}
                                dontOrderFirstCollumn={true}
                            />
                        </div>
                    </div>
                </div>
            </Layout>
        </>
    );
}
